/**
 * RFC 5646 language tag → human-readable label mapping.
 *
 * Ported from `legacy/js/language_tags.js` to TypeScript. Provides a typed
 * `Record<string, string>` map of IETF BCP 47 language codes to display
 * labels, consumed by `tracks-handler` when building the audio/subtitle
 * track lists shown to the user.
 *
 * Key improvements over the legacy implementation:
 * - Fully typed with `as const` for compile-time safety and autocomplete
 * - Single exported helper `getLanguageLabel()` with a sensible fallback
 * - No external dependencies
 */

/**
 * Map of RFC 5646 language tags to human-readable labels.
 *
 * Covers the full set of codes that appear in the BP catalog (VOD and LIVE)
 * plus common regional variants. The `as const` assertion enables TypeScript
 * to narrow keys for autocomplete while still allowing any `string` lookup
 * via the exported helper function.
 */
const LANGUAGE_TAGS = {
  // Portuguese
  'pt': 'Português',
  'pt-BR': 'Português (Brasil)',
  'pt-PT': 'Português (Portugal)',

  // English
  'en': 'English',
  'en-US': 'English (US)',
  'en-GB': 'English (UK)',
  'en-AU': 'English (Australia)',

  // Spanish
  'es': 'Español',
  'es-ES': 'Español (España)',
  'es-MX': 'Español (México)',
  'es-AR': 'Español (Argentina)',
  'es-419': 'Español (Latinoamérica)',

  // French
  'fr': 'Français',
  'fr-FR': 'Français (France)',
  'fr-CA': 'Français (Canada)',

  // German
  'de': 'Deutsch',
  'de-DE': 'Deutsch (Deutschland)',
  'de-AT': 'Deutsch (Österreich)',

  // Italian
  'it': 'Italiano',
  'it-IT': 'Italiano (Italia)',

  // Japanese
  'ja': '日本語',
  'ja-JP': '日本語 (日本)',

  // Korean
  'ko': '한국어',
  'ko-KR': '한국어 (대한민국)',

  // Chinese
  'zh': '中文',
  'zh-CN': '中文 (简体)',
  'zh-TW': '中文 (繁體)',
  'zh-Hans': '中文 (简体)',
  'zh-Hant': '中文 (繁體)',

  // Russian
  'ru': 'Русский',
  'ru-RU': 'Русский (Россия)',

  // Arabic
  'ar': 'العربية',
  'ar-SA': 'العربية (السعودية)',

  // Hebrew
  'he': 'עברית',
  'he-IL': 'עברית (ישראל)',

  // Dutch
  'nl': 'Nederlands',
  'nl-NL': 'Nederlands (Nederland)',

  // Polish
  'pl': 'Polski',
  'pl-PL': 'Polski (Polska)',

  // Turkish
  'tr': 'Türkçe',
  'tr-TR': 'Türkçe (Türkiye)',

  // Swedish
  'sv': 'Svenska',
  'sv-SE': 'Svenska (Sverige)',

  // Norwegian
  'no': 'Norsk',
  'nb': 'Norsk Bokmål',
  'nn': 'Norsk Nynorsk',

  // Danish
  'da': 'Dansk',
  'da-DK': 'Dansk (Danmark)',

  // Finnish
  'fi': 'Suomi',
  'fi-FI': 'Suomi (Suomi)',

  // Greek
  'el': 'Ελληνικά',
  'el-GR': 'Ελληνικά (Ελλάδα)',

  // Romanian
  'ro': 'Română',
  'ro-RO': 'Română (România)',

  // Hungarian
  'hu': 'Magyar',
  'hu-HU': 'Magyar (Magyarország)',

  // Czech
  'cs': 'Čeština',
  'cs-CZ': 'Čeština (Česko)',

  // Thai
  'th': 'ไทย',
  'th-TH': 'ไทย (ไทย)',

  // Vietnamese
  'vi': 'Tiếng Việt',
  'vi-VN': 'Tiếng Việt (Việt Nam)',

  // Indonesian
  'id': 'Bahasa Indonesia',
  'id-ID': 'Bahasa Indonesia (Indonesia)',

  // Malay
  'ms': 'Bahasa Melayu',
  'ms-MY': 'Bahasa Melayu (Malaysia)',

  // Hindi
  'hi': 'हिन्दी',
  'hi-IN': 'हिन्दी (भारत)',

  // Ukrainian
  'uk': 'Українська',
  'uk-UA': 'Українська (Україна)',

  // Latin (used in some documentary/classical content)
  'la': 'Latina',

  // Undetermined / special
  'und': 'Desconhecido',
  'mul': 'Múltiplos idiomas',
  'zxx': 'Sem conteúdo linguístico',
} as const;

/** Union type of all known language codes in the map. */
export type LanguageCode = keyof typeof LANGUAGE_TAGS;

/**
 * Returns a human-readable label for the given RFC 5646 language code.
 *
 * Lookup strategy:
 * 1. Exact match in the map (e.g. `"pt-BR"` → `"Português (Brasil)"`)
 * 2. Base language fallback — strips the region subtag and retries
 *    (e.g. `"en-NZ"` → tries `"en"` → `"English"`)
 * 3. Returns the raw code if nothing matched (e.g. `"xh"` → `"xh"`)
 *
 * @param code - An IETF BCP 47 / RFC 5646 language tag
 * @returns The display label or the raw code as fallback
 */
export function getLanguageLabel(code: string): string {
  const tags: Record<string, string> = LANGUAGE_TAGS;

  // 1. Exact match
  const exact = tags[code];
  if (exact !== undefined) return exact;

  // 2. Base language fallback (e.g. "pt-BR" → "pt")
  const base = code.split('-')[0];
  if (base !== undefined && base !== code) {
    const baseMatch = tags[base];
    if (baseMatch !== undefined) return baseMatch;
  }

  // 3. Raw code as last resort
  return code;
}
